import { ref } from 'vue';
import useContents from '@/core/useContents';
import useStructures from '@/core/useStructures';
import useTenant from '@/core/useTenant';

import { sortByProperty } from '@/utils/sort';
import { Asset, useAssets } from '@/core';
import useGql from '@/services/useGql';
import {
  Audio,
  CMSStructureItem,
  CMSContent,
  CMSGenre,
  Enable,
  Example,
  Genre,
  Selection,
  Task,
  Video,
  CMSTaskToolbox,
  Toolbox,
} from '@/model';
import { TenantConfig } from '@/types/interface/TenantConfig.interface';
import { useMemoize } from '@vueuse/core';
import { ResponseErrorStatusCode } from '@/services/useError';
import { parseDashedColor } from '@/utils/parseDashedColor';
import { useRouter } from 'vue-router';

const genres = ref<Genre[]>([]);
const selectedExamples = ref<Example[]>([]);
const selectedTasks = ref<Task[]>([]);
const isLoading = ref<boolean>(false);
const genre = ref<Genre>(null);

const { getObjectById } = useContents();
const { structures, fetchStructuresChildren } = useStructures();
const { tenant } = useTenant<TenantConfig>();
const { fetchAsset, assets: assetApiResponse } = useAssets();
const { fetchObject } = useGql();

const getImgUrlsInGenre = () => {
  const text = genres.value.flatMap((genre) => {
    const bodyText = genre.bodyText;
    const tasks = genre.tasks.map((task) => task.text);
    const examples = genre.examples.map((example) => example.text);

    return [bodyText, ...tasks, ...examples];
  });

  const dummyDiv = document.createElement('DIV');
  dummyDiv.innerHTML = text.join('');

  return Array.from(dummyDiv.querySelectorAll('img'))
    .map((img) => {
      if (img.src.startsWith('https')) {
        return img.src;
      }
    })
    .filter(Boolean);
};

const prefetchImages = (assets: Asset[]) => {
  const assetTypesForPreload = ['image'];
  new Set();
  const imageUrls = new Set([
    ...assets
      .map((asset) => {
        if (assetTypesForPreload.includes(asset.type)) {
          return asset.url;
        }
      })
      .filter(Boolean),
    ...getImgUrlsInGenre(),
  ]);

  Promise.allSettled(Array.from(imageUrls).map((url) => fetch(url)));
};

const filterGenresBySlug = (o: CMSStructureItem): boolean =>
  o.contentId && tenant.value.slugs.map((slug) => `${slug}/genres`).some((slug) => o.slugPath.startsWith(slug));

const normalizeContent = (content: CMSGenre): CMSGenre => {
  return {
    bodyText: '',
    dispositions: [],
    examples: [],
    heading: '',
    name: '',
    recording: [],
    showDispositionsInPreview: false,
    showHeaderInPreview: false,
    startToWrite: false,
    tasks: [],
    videos: [],
    ...content,
    checklist: {
      enabled: content.checklist?.enabled || false,
      options: content.checklist?.options || [],
      usage: {
        step1: content.checklist?.usage?.step1 || false,
        step2: content.checklist?.usage?.step2 || false,
      },
    },
    attachments: { enabled: content.attachments?.enabled || false },
    selectedExamples: {
      selected: content.selectedExamples?.selected || [],
      enabled: content.selectedExamples?.enabled || false,
    },
    selectedTasks: {
      selected: content.selectedTasks?.selected || [],
      enabled: content.selectedTasks?.enabled || false,
    },
    toolbox: {
      ...content.toolbox,
      enabled: content.toolbox?.enabled || false,
    },
  };
};

const loadGenres = useMemoize(async (language: string): Promise<void> => {
  isLoading.value = true;

  try {
    await Promise.all(
      tenant.value.slugs.map((slug) =>
        fetchStructuresChildren(`${slug}/genres`, { limit: 100, filter: '{"type": "OBJECT"}' }),
      ),
    );
  } catch (error) {
    if (error.status.toString() === ResponseErrorStatusCode.UNAUTHORIZED) {
      useRouter().push('/login');
    } else {
      throw error;
    }
  }

  const genreIds = structures.value.filter(filterGenresBySlug).map((o: CMSStructureItem) => o.contentId);

  await Promise.all(genreIds.map((id) => fetchObject(id)));

  const assetsIds: string[] = [
    ...genreIds
      .map(getObjectById)
      .map((genre) => normalizeContent(genre.content))
      .map((normalizedContent) => getGenreAssetIds(normalizedContent))
      .reduce((result, part) => [...result, ...part], []),
    ...genreIds
      .map((id) => getObjectById(id, 'nn'))
      .map((genre) => normalizeContent(genre.content))
      .map((normalizedContent) => getGenreAssetIds(normalizedContent))
      .reduce((result, part) => [...result, ...part], []),
  ];

  await setGenresByLang(language);
  await loadAssets(assetsIds);

  isLoading.value = false;
});

const setGenresByLang = async (language): Promise<void> => {
  const objects = await Promise.all(
    structures.value
      .filter(filterGenresBySlug)
      .map((o: CMSStructureItem) => o.contentId)
      .map((id) => getObjectById(id, language)),
  );

  genres.value = [
    ...objects.map((item: CMSContent<CMSGenre>) => {
      const structure = structures.value.find((structure) => structure.contentId === item.id);

      return convertGenre(item.content, structure, tenant.value.slugs);
    }),
  ].sort(sortByProperty('order'));

  if (genre.value) {
    genre.value = genres.value.find((g) => g.id === genre.value.id);
  }
};

const getGenreAssetIds = (genre: CMSGenre): string[] =>
  [
    ...getVideoAssetIds(genre.videos),
    ...getVideoAssetIds(genre.toolbox?.videos),
    genre.toolbox?.image?.image,
    ...genre.tasks.flatMap((task) => [
      task.coverImage,
      task.toolbox?.dispositionImage?.image,
      ...getVideoAssetIds(task.toolbox?.video),
    ]),
    ...getRecordingAssetIds(genre),
  ].filter(Boolean);

const loadAssets = async (assets: string[]): Promise<void> => {
  try {
    await Promise.allSettled(Array.from(new Set(assets)).map((id) => fetchAsset(id)));

    prefetchImages(assetApiResponse.value);
  } catch (e) {
    console.error(e.message);
  }
};

const getVideoAssetIds = (videos: Video[] = []): string[] => {
  return videos?.reduce(
    (assets: string[], video: Video): string[] => [
      ...assets,
      video.videoId,
      ...(video.images?.map((image) => image.imageId) ?? []),
      ...(video.textTracks?.map((track) => track.textTrackId) ?? []),
    ],
    [],
  );
};

const getRecordingAssetIds = (genre: CMSGenre): string[] =>
  [
    ...extractAudioIds(genre),
    ...genre.tasks.reduce((ids, task) => [...ids, ...extractAudioIds(task)], []),
    ...genre.examples.reduce((ids, example) => [...ids, ...extractAudioIds(example)], []),
  ].filter(Boolean);

const extractAudioIds = <A extends { recording: Audio[] }>(item: A): string[] =>
  item.recording ? item.recording.map((audio) => audio.audioId) : [];

const flattenRecording = <A extends { recording: Audio[] }>(
  input: A,
): Pick<A, Exclude<keyof A, 'recording'>> & { recording?: Audio } => {
  const { recording, ...otherValues } = input;
  return {
    ...otherValues,
    recording: recording && recording.length ? recording[0] : undefined,
  };
};

const normalizeTaskToolbox = (cmsToolbox: CMSTaskToolbox & Enable): Toolbox & Enable => {
  if (!cmsToolbox) return;

  return {
    enabled: cmsToolbox.enabled,
    image: cmsToolbox.dispositionImage,
    videos: cmsToolbox.video,
    audio: cmsToolbox.audioFile && cmsToolbox.audioFile.length ? cmsToolbox.audioFile[0] : undefined,
    exampleId: cmsToolbox.selectedExamples?.selected[0],
    options: {
      useTaskTitle: cmsToolbox.useTaskTitle,
      useCoverImage: cmsToolbox.useCoverImage,
    },
  };
};

const convertGenre = (genre: CMSGenre, structure: CMSStructureItem, tenantSlugs: string[]): Genre => {
  const normalizedGenre = normalizeContent(genre);
  const allExamples = normalizedGenre.examples?.map(flattenRecording)?.map((example) => {
    return {
      ...example,
      highlights: example.highlights?.map((highlight) => {
        return { ...highlight, colorConfig: parseDashedColor(highlight.color) };
      }),
    };
  });

  return {
    name: normalizedGenre.name.toLowerCase(),
    heading: normalizedGenre.heading,
    bodyText: normalizedGenre.bodyText,
    startToWrite: normalizedGenre.startToWrite,
    dispositions: normalizedGenre.dispositions.map((disposition) => ({
      ...disposition,
      colorConfig: parseDashedColor(disposition.color),
    })),
    toolbox: normalizedGenre.toolbox,
    checklist: normalizedGenre.checklist,
    attachments: normalizedGenre.attachments || { enabled: false },
    video: normalizedGenre.videos && normalizedGenre.videos.length ? normalizedGenre.videos[0] : undefined,
    recording: normalizedGenre.recording && normalizedGenre.recording.length ? normalizedGenre.recording[0] : undefined,
    allExamples,
    examples: selectExamplesOrTasks(normalizedGenre.selectedExamples, allExamples),
    tasks: selectExamplesOrTasks(normalizedGenre.selectedTasks, normalizedGenre.tasks?.map(flattenRecording))?.map(
      (task: Task) => ({
        ...task,
        to: `/${structure.slug}/task/${task.id}`,
        toolbox: normalizeTaskToolbox(task.toolbox),
      }),
    ),
    id: structure.contentId,
    slug: structure.slug,
    order: structure.order + tenantSlugs.findIndex((tenantSlug) => structure.slugPath.startsWith(tenantSlug)) * 100,
    displayHeaders: {
      genreTitle: normalizedGenre.showHeaderInPreview,
      dispositionTitles: normalizedGenre.showDispositionsInPreview,
    },
  };
};

const selectGenre = (slug: string) => {
  genre.value = genres.value.find((item) => item.slug === slug);
  return genre.value;
};

const selectExamplesOrTasks = <T extends { id: string }>(selection: Selection & Enable, entries: T[]): T[] => {
  if (!selection.enabled) {
    return [];
  }

  return selection.selected?.map((id) => entries.find((item) => item.id === id)).filter(Boolean);
};

const selectTask = (genreSlug: string, taskId: string) => {
  const genre = selectGenre(genreSlug);

  if (genre) {
    return genre.tasks.find((task) => task.id === taskId);
  }

  return null;
};

export default () => ({
  genre,
  genres,
  isLoading,
  selectedExamples,
  selectedTasks,
  setGenresByLang,
  selectGenre,
  selectTask,
  loadGenres,
});
